import React, {useEffect, useRef, useState} from 'react';
import { useLocation, useNavigate } from 'react-router';
import CardHeader from '../../components/common/card-header/CardHeader';
import {
  Card,
  FlexItem,
  FlexLayout, Spinner,
  StackLayout, Text,
} from '@salt-ds/core';

import StepTracker from '../../components/common/StepTracker';
import RegisterComputerVerifyForm from './RegisterComputerVerifyForm';
import CardAlert from '../../components/common/card-alert/CardAlert';
import '../CommonCard.css';
import Constants from '../../components/common/Constants';
import {useIntl} from 'react-intl';

function RegisterComputerVerify() {
  const location = useLocation();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState(""); // Fix useState declaration
  const { userId, orgId, deviceSignature, secretPhrase } = location.state || {};
  const [isAccessCodeButtonClicked, setIsAccessCodeButtonClicked,] = useState(false);
  const [isAnotherAccessCodeButtonClicked, setIsAnotherAccessCodeButtonClicked,] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSpinnerVisible, setIsSpinnerVisible] = useState(false);
  const mainRef = useRef(null);

  const showErrorOnPage = (error)=> {
    setErrorMessage(error);
    setHasError(true);
  }

  const getBannerStatus = () => {
    if (hasError) return "error";
    if (isAnotherAccessCodeButtonClicked) return "success";
    return "info";
  };

  const getBannerMessage = () => {
    if (hasError) return Constants.invalidAccessCodeError;
    if (isAnotherAccessCodeButtonClicked) return Constants.registerComputerStep1AnotherAccessCodeMessage;
    return Constants.registerComputerStep1AccessCodeMessage;
  };

  const [headerContent, setHeaderContent] = useState(
    <CardAlert
      setIsAccessCodeButtonClicked={setIsAccessCodeButtonClicked}
      setIsAnotherAccessCodeButtonClicked={setIsAnotherAccessCodeButtonClicked}
      setHasError={setHasError}
      setIsSpinnerVisible={setIsSpinnerVisible}
      status={getBannerStatus()}>
      {getBannerMessage()}
    </CardAlert>
  );

  useEffect(() => {
    if (!isSpinnerVisible) {
      setHeaderContent(
        <CardAlert
          setIsAccessCodeButtonClicked={setIsAccessCodeButtonClicked}
          setIsAnotherAccessCodeButtonClicked={setIsAnotherAccessCodeButtonClicked}
          setHasError={setHasError}
          setIsSpinnerVisible={setIsSpinnerVisible}
          status={getBannerStatus()}>
          {getBannerMessage()}
        </CardAlert>
      );
    }
  }, [hasError, isAnotherAccessCodeButtonClicked, isSpinnerVisible]);

  const showSpinner = ()=> {
    setIsSpinnerVisible(true);
    setHeaderContent(
        <FlexItem className="spinner-wrapper">
          <Spinner data-testid="spinner-loading" aria-label="loading" role="status" size="medium" style={{marginLeft: "50%"}} />
        </FlexItem>
    )
  }

  const hideSpinnerAndShowError = ()=> {
    setIsSpinnerVisible(false);
    setHasError(true);
    setIsAccessCodeButtonClicked(false);
    setIsAnotherAccessCodeButtonClicked(false);
  }

  const hideSpinnerAndShowAnotherAccessCodeMessage = () => {
    setIsSpinnerVisible(false);
    setHasError(false);
    setIsAccessCodeButtonClicked(false);
    setIsAnotherAccessCodeButtonClicked(true);
  }

  const intl = useIntl();
  useEffect(() => {
    document.title = intl.formatMessage({id: 'logon.ui.registercomputer.step1.page.title'});
    // Set focus to main element for screen reader announcement
    if (mainRef.current) {
      mainRef.current.focus();
    }

    const expectedPreviousPage = 'loginPage';
    if (location.state?.from !== expectedPreviousPage) {
      navigate('/');
    }
  }, [location, navigate]);

  return (
    <main ref={mainRef} role="main" aria-label="Register Computer Verify" tabIndex="-1">
    <FlexLayout  className={"page-content"}>
      <FlexItem className={`error-wrapper ${(!isAccessCodeButtonClicked || isAnotherAccessCodeButtonClicked) || hasError || isSpinnerVisible? 'visible' : ''}`}>
        <div>
          {headerContent}
        </div>
      </FlexItem>
      <FlexItem>
        <Card className="card-wrapper">
          <FlexItem className="card-content">
            <StackLayout gap={5}>
              <CardHeader heading={Constants.registerComputerTitle} subheading={Constants.registerComputerStep1Title} style={{maxWidth: "20rem"}}/>
              <StepTracker label1={intl.formatMessage({id: 'logon.ui.registercomputer.flow.step1'})} step1="active"
                label2={intl.formatMessage({id: 'logon.ui.registercomputer.flow.step2'})} step2="pending"
                label3={intl.formatMessage({id: 'logon.ui.registercomputer.flow.step3'})} step3="pending" />
              <FlexItem>
                <Text >
                  {Constants.registerComputerStep1InstructionHeader}
                </Text>
              </FlexItem>
              <RegisterComputerVerifyForm isAccessCodeButtonClicked={isAccessCodeButtonClicked}
                setIsAccessCodeButtonClicked={setIsAccessCodeButtonClicked}
                isAnotherAccessCodeButtonClicked={isAnotherAccessCodeButtonClicked}
                setIsAnotherAccessCodeButtonClicked={setIsAnotherAccessCodeButtonClicked}
                userId={userId} orgId={orgId} deviceSignature={deviceSignature}
                showSpinner={showSpinner}  secretPhrase={secretPhrase} setHasError={setHasError}
                hideSpinnerAndShowError={hideSpinnerAndShowError}
                hideSpinnerAndShowAnotherAccessCodeMessage={hideSpinnerAndShowAnotherAccessCodeMessage}
                showErrorOnPage={showErrorOnPage} isLoading={isLoading} setIsLoading={setIsLoading}
              />
            </StackLayout>
          </FlexItem>
        </Card>
      </FlexItem>
    </FlexLayout>
    </main>
  );
}

export default RegisterComputerVerify;